import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type CapabilityState, type Connection, fmtDate, type IntegrationsResponse, useApi } from '../api';
import type { TagClass } from '../data/types';
import { Icon, Tag } from '../ui';

/**
 * Integrations (P14-10, §5.9). Meta section is LIVE: real connection state, linked accounts and per-capability state from
 * the probes. Permission problems are shown separately from connection state. No fake toggles. First-party, local AI and
 * notification cards come from the brand status read model.
 */
const CAP_LABEL: Record<string, string> = {
  'ig.owned_media.read': 'Read Instagram posts',
  'ig.insights.read': 'Instagram insights',
  'fb.page.read': 'Read Facebook Page',
  'fb.organic.insights': 'Facebook posts',
  'leads.retrieve': 'Lead forms',
  'ads.hierarchy.read': 'Ad campaigns',
  'ads.insights.read': 'Ads insights',
};
const STATE_LABEL: Record<CapabilityState, string> = {
  SUPPORTED: 'available',
  PERMISSION_MISSING: 'permission missing',
  UNSUPPORTED: 'not supported',
  NOT_APPLICABLE: 'not applicable',
  TEMPORARILY_UNAVAILABLE: 'temporarily unavailable',
  DEPRECATED: 'deprecated',
  UNKNOWN: 'unknown',
};
const stateTag = (s: CapabilityState): TagClass => (s === 'SUPPORTED' ? 'tag-accent' : s === 'NOT_APPLICABLE' ? 'tag-neutral' : 'tag-outline');
const connTag = (s: Connection['status']): TagClass => (s === 'CONNECTED' ? 'tag-accent' : s === 'DISCONNECTED' || s === 'NOT_CONNECTED' ? 'tag-neutral' : 'tag-outline');
const KIND_LABEL = { FB_PAGE: 'Facebook Page', IG_PROFESSIONAL: 'Instagram', META_AD_ACCOUNT: 'Ad account' } as const;
const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(',', '') : '—');

const CONNECT_MESSAGES: Record<string, string> = {
  ok: 'Connected. Marketing OS is now checking what each account allows.',
  cancelled: 'Connection cancelled on the Meta screen. Nothing was changed.',
  failed: 'Meta refused the connection. Check the app settings and try again.',
};

interface BrandStatus {
  localAi: { connected: boolean; models: string[]; capabilities: string[]; queuedJobs: number };
  commerce: { name: string; provider: string; status: string; lastSyncedAt: string | null }[];
  leads: { capability: string };
  capi: { enabled: boolean };
  notifications: { channel: string; enabled: boolean; verified: boolean }[];
}

export function Integrations({ brandId, brandName, canAdmin }: { brandId: string; brandName: string; canAdmin: boolean }) {
  const status = useApi<BrandStatus>(`/api/v1/brands/${brandId}/status`);
  const [state, setState] = useState<IntegrationsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(() => {
    const q = new URLSearchParams(window.location.search);
    const c = q.get('connect');
    return c ? (CONNECT_MESSAGES[c] ?? null) + (q.get('reason') ? ` (${q.get('reason')})` : '') : null;
  });

  const load = useCallback(async () => {
    try {
      setState(await api<IntegrationsResponse>('GET', `/api/v1/brands/${brandId}/integrations`));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load integrations.');
    }
  }, [brandId]);

  useEffect(() => { void load(); }, [load]);

  // While probes are pending (connected but not yet checked), refresh every 3 s for up to a minute.
  const pending = state?.connections.some((c) => c.status !== 'DISCONNECTED' && c.capabilities.length === 0) ?? false;
  useEffect(() => {
    if (!pending) return;
    let n = 0;
    const t = setInterval(() => { if (++n > 20) clearInterval(t); void load(); }, 3000);
    return () => clearInterval(t);
  }, [pending, load]);

  const act = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try { await fn(); } catch (e) { setError(e instanceof ApiError ? `${e.message}` : 'Action failed.'); } finally { setBusy(false); }
  };
  const connect = (reconnectConnectionId?: string) => act(async () => {
    const r = await api<{ authorizeUrl: string }>('POST', `/api/v1/brands/${brandId}/integrations/meta/connect`, { authMode: 'FACEBOOK_LOGIN', ...(reconnectConnectionId ? { reconnectConnectionId } : {}) });
    window.location.href = r.authorizeUrl; // Meta's permission screen; it returns to the API callback
  });
  const recheck = (id: string) => act(async () => {
    await api('POST', `/api/v1/integrations/${id}/recheck`);
    setFlash('Recheck queued. Results appear here in a few seconds.');
    setTimeout(() => void load(), 3000);
    setTimeout(() => void load(), 8000);
  });
  const release = (connectionId: string, accountId: string, label: string) => act(async () => {
    if (!window.confirm(`Remove "${label}" from ${brandName}? It stops being collected for this brand and becomes available to another brand. History is kept.`)) return;
    await api('POST', `/api/v1/integrations/${connectionId}/accounts/${accountId}/release`);
    await load();
  });
  const disconnect = (id: string) => act(async () => {
    if (!window.confirm('Disconnect Meta for this brand? Stored tokens are deleted; history is kept.')) return;
    await api('POST', `/api/v1/integrations/${id}/disconnect`);
    await load();
  });

  const live = state?.connections.filter((c) => c.status !== 'DISCONNECTED') ?? [];
  const st = status.data;
  const otherGroups: { name: string; items: { name: string; icon: string; status: string; tagClass: TagClass; desc: string; meta: string }[] }[] = st ? [
    { name: 'First-party data', items: [
      ...(st.commerce.length ? st.commerce.map((c) => ({ name: c.name, icon: 'ph-ticket', status: c.status.toLowerCase(), tagClass: (c.status === 'ACTIVE' ? 'tag-accent' : 'tag-outline') as TagClass, desc: `${c.provider === 'WOOCOMMERCE' ? 'WooCommerce shop (read-only API key)' : 'CSV import'} — orders, refunds and ticket products.`, meta: `Last sync ${fmtDate(c.lastSyncedAt)}` }))
        : [{ name: 'Ticket / shop source', icon: 'ph-ticket', status: 'not connected', tagClass: 'tag-neutral' as TagClass, desc: 'Connect the shop that sells tickets (WooCommerce read-only key) or import a CSV to see tickets, revenue and ticket pace.', meta: 'Needed for ticket goals and attribution' }]),
      { name: 'Meta lead forms', icon: 'ph-address-book', status: st.leads.capability === 'SUPPORTED' ? 'available' : st.leads.capability === 'PERMISSION_MISSING' ? 'permission missing' : 'not checked', tagClass: (st.leads.capability === 'SUPPORTED' ? 'tag-accent' : 'tag-outline') as TagClass, desc: 'Leads from Meta lead ads, stored with encrypted contact details.', meta: st.leads.capability === 'PERMISSION_MISSING' ? 'Needs the Leads use case / leads_retrieval on the Meta app' : 'Synced hourly while available' },
      { name: 'Conversions API', icon: 'ph-share-network', status: st.capi.enabled ? 'on' : 'off', tagClass: (st.capi.enabled ? 'tag-accent' : 'tag-neutral') as TagClass, desc: 'Sends consented purchase events to your Meta dataset.', meta: 'Off by default · needs dataset, token and a consent decision' },
    ] },
    { name: 'Local AI', items: [
      { name: 'Local AI worker (LM Studio)', icon: 'ph-cpu', status: st.localAi.connected ? 'connected' : 'offline', tagClass: (st.localAi.connected ? 'tag-accent' : 'tag-outline') as TagClass, desc: 'Answers Studio questions and labels images on your own PC; pulls jobs over HTTPS.', meta: st.localAi.connected ? `Models: ${st.localAi.models.slice(0, 3).join(', ') || '—'} · ${st.localAi.queuedJobs} queued` : `${st.localAi.queuedJobs} job(s) waiting · start LM Studio and run the bridge (npm run bridge)` },
    ] },
    { name: 'Notifications', items: st.notifications.map((n) => ({ name: n.channel === 'IN_APP' ? 'In-app' : n.channel === 'EMAIL' ? 'Email (SMTP)' : 'Telegram', icon: n.channel === 'IN_APP' ? 'ph-bell' : n.channel === 'EMAIL' ? 'ph-envelope' : 'ph-telegram-logo', status: n.enabled ? (n.verified ? 'verified' : 'enabled') : 'off', tagClass: (n.enabled ? 'tag-accent' : 'tag-neutral') as TagClass, desc: n.channel === 'IN_APP' ? 'Alerts and digests inside Marketing OS.' : 'Used only by approved automations (alerts, digests).', meta: n.channel === 'IN_APP' ? 'Always on' : 'Configure in channel settings (admin)' })) },
  ] : [];

  return (
    <>
      <header style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <h6 style={{ color: 'var(--color-accent)', margin: 0 }}>{brandName} · direct platform APIs, no paid middleware</h6>
        <h2 style={{ margin: 0 }}>Integrations</h2>
      </header>

      {flash && <div className="card elev-sm" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}><Icon name="ph-info" style={{ color: 'var(--color-accent)' }} /><span style={{ flex: 1 }}>{flash}</span><button className="btn btn-ghost" onClick={() => { setFlash(null); window.history.replaceState(null, '', '/?page=integrations'); }}>Dismiss</button></div>}
      {error && <div className="card elev-sm" style={{ flexDirection: 'row', gap: 10, color: 'var(--color-accent-300)' }}><Icon name="ph-warning" /><span>{error}</span></div>}

      <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <h4 style={{ margin: 0, fontSize: 17 }}>Meta</h4>
        {!state && !error && <p className="text-muted">Loading…</p>}
        {state && !state.metaAppConfigured && (
          <div className="card elev-sm"><p className="card-body">No Meta app is configured for this installation yet (MOS_META_APP_ID / SECRET / REDIRECT in .env).</p></div>
        )}
        {state && state.metaAppConfigured && live.length === 0 && (
          <div className="card elev-sm" style={{ gap: 'var(--space-2)', maxWidth: 560 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Icon name="ph-facebook-logo" style={{ fontSize: 20, color: 'var(--color-accent)' }} /><span className="card-title" style={{ fontSize: 15 }}>Meta · Facebook Login</span><Tag cls="tag-neutral" style={{ marginLeft: 'auto' }}>not connected</Tag></div>
            <p className="card-body">Connects the {brandName} Facebook Page, its linked Instagram professional account and ad accounts, read-only. You choose the Pages and accounts on Meta's screen.</p>
            <div className="card-meta"><span>Requests read permissions only · no publishing or ad changes</span></div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-primary" disabled={!canAdmin || busy} onClick={() => connect()} title={canAdmin ? '' : 'Brand admin role required'}><Icon name="ph-plugs" />Connect with Facebook</button>
            </div>
          </div>
        )}
        {live.map((c) => {
          const accountName = (id: string | null) => { const a = c.accounts.find((x) => x.id === id); return a ? `${KIND_LABEL[a.kind]} · ${a.username ? '@' + a.username : a.name ?? a.externalId}` : '—'; };
          const problems = c.capabilities.filter((x) => x.state === 'PERMISSION_MISSING' || x.state === 'TEMPORARILY_UNAVAILABLE' || x.state === 'UNKNOWN').length;
          return (
            <div key={c.id} className="card elev-sm" style={{ gap: 'var(--space-3)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Icon name="ph-facebook-logo" style={{ fontSize: 20, color: 'var(--color-accent)' }} />
                <span className="card-title" style={{ fontSize: 15 }}>Meta · {c.authMode === 'FACEBOOK_LOGIN' ? 'Facebook Login' : 'Instagram Login'}</span>
                <Tag cls={connTag(c.status)}>{c.status.toLowerCase()}</Tag>
                {problems > 0 && <Tag cls="tag-outline">{problems} capability issue{problems > 1 ? 's' : ''}</Tag>}
                <span className="text-muted" style={{ marginLeft: 'auto', fontSize: 12 }}>API {c.apiVersion} · checked {fmt(c.lastValidatedAt)}{c.expiresAt ? ` · token until ${fmt(c.expiresAt).slice(0, 10)}` : ''}</span>
              </div>
              {(c.status === 'EXPIRED' || c.status === 'REVOKED') && <p className="card-body" style={{ color: 'var(--color-accent-300)' }}>Meta no longer accepts this connection's token (error {c.lastErrorCode}). Collection is paused; history is kept. Reconnect to resume.</p>}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {c.accounts.map((a) => (
                  <span key={a.id} className="tag tag-neutral" style={{ whiteSpace: 'nowrap', gap: 6 }}>
                    {accountName(a.id)}
                    {canAdmin && <button title={`Not a ${brandName} account — release it`} disabled={busy} onClick={() => release(c.id, a.id, accountName(a.id))} style={{ border: 0, background: 'transparent', color: 'inherit', cursor: 'pointer', padding: 0, display: 'grid', placeItems: 'center', opacity: 0.7 }}><Icon name="ph-x" style={{ fontSize: 11 }} /></button>}
                  </span>
                ))}
              </div>
              {canAdmin && c.accounts.length > 0 && <div className="card-meta"><span>Meta lists every account your Facebook user can reach. Remove (✕) any that are not {brandName}'s; they become unassigned and can be connected to another brand.</span></div>}
              {c.capabilities.length === 0 ? (
                <p className="card-body">Checking what Meta allows for each account… (the worker must be running)</p>
              ) : (
                <table className="table" style={{ fontSize: 13 }}>
                  <thead><tr><th>Capability</th><th>Account</th><th>State</th><th>Checked</th></tr></thead>
                  <tbody>
                    {c.capabilities.map((x) => (
                      <tr key={x.capabilityId + (x.account ?? '')}>
                        <td>{CAP_LABEL[x.capabilityId] ?? x.capabilityId}</td>
                        <td className="text-muted">{accountName(x.account)}</td>
                        <td><Tag cls={stateTag(x.state)}>{STATE_LABEL[x.state]}{x.metaErrorCode ? ` · #${x.metaErrorCode}` : ''}</Tag></td>
                        <td className="text-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(x.checkedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {c.declinedScopes.length > 0 && <div className="card-meta"><span>Not granted on Meta's screen: {c.declinedScopes.join(', ')}</span></div>}
              {canAdmin && (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-secondary" disabled={busy} onClick={() => recheck(c.id)}><Icon name="ph-arrows-clockwise" />Recheck</button>
                  <button className="btn btn-secondary" disabled={busy} onClick={() => connect(c.id)}>Reconnect</button>
                  <button className="btn btn-ghost" disabled={busy} onClick={() => disconnect(c.id)}>Disconnect</button>
                </div>
              )}
            </div>
          );
        })}
      </section>

      {canAdmin && <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', maxWidth: 760 }}><h4 style={{ margin: 0, fontSize: 17 }}>Door sales</h4><VippsDoorImport brandId={brandId} /></section>}
      {otherGroups.map((g) => (
        <section key={g.name} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <h4 style={{ margin: 0, fontSize: 17 }}>{g.name}</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 'var(--space-3)' }}>
            {g.items.map((it) => (
              <div key={it.name} className="card elev-sm" style={{ gap: 'var(--space-2)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Icon name={it.icon} style={{ fontSize: 20, color: 'var(--color-accent)' }} /><span className="card-title" style={{ fontSize: 15 }}>{it.name}</span><Tag cls={it.tagClass} style={{ marginLeft: 'auto' }}>{it.status}</Tag></div>
                <p className="card-body">{it.desc}</p>
                <div className="card-meta"><span>{it.meta}</span></div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

interface DoorPlan { dryRun: boolean; columns: Record<string, string | null>; rows: number; assigned: { eventId: number; eventTitle: string; payments: number; amount: number; tickets: number; estimated: number }[]; skipped: Record<string, number>; problems: string[]; CREATED?: number; UPDATED?: number; UNCHANGED?: number }

/** Vipps door sales from a portal CSV export: preview first (nothing stored), then import. */
export function VippsDoorImport({ brandId, onImported }: { brandId: string; onImported?: () => void }) {
  const [csv, setCsv] = useState('');
  const [xlsx, setXlsx] = useState('');
  const [fileName, setFileName] = useState('');
  const [price, setPrice] = useState('');
  const [plan, setPlan] = useState<DoorPlan | null>(null);
  const [err, setErr] = useState('');
  const run = async (dryRun: boolean) => {
    setErr('');
    try { setPlan(await api<DoorPlan>('POST', `/api/v1/brands/${brandId}/commerce/vipps-door`, { ...(xlsx ? { xlsxBase64: xlsx } : { csv }), dryRun, ...(price ? { doorPrice: Number(price) } : {}) })); if (!dryRun) onImported?.(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Failed'); }
  };
  const skipLabel: Record<string, string> = { outsideEventWindows: 'outside this brand\'s event nights', onlineShopPayments: 'online shop payments (already counted)', refunds: 'refunds (review manually)', unparseable: 'unreadable rows', zeroAmount: 'zero amount', otherOrganisers: 'products of other organisers' };
  return (
    <div className="card elev-sm" style={{ gap: 'var(--space-2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Icon name="ph-door-open" style={{ fontSize: 20, color: 'var(--color-accent)' }} /><span className="card-title" style={{ fontSize: 15 }}>Vipps door sales (product report or CSV)</span></div>
      <p className="card-body">Upload the Vipps Produktrapport (.xlsx) for one event night — exact ticket counts per organiser — or a transaction CSV. Payments are matched to event nights by time; online shop payments are left out. Preview first — nothing is stored until you import.</p>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <input type="file" accept=".csv,text/csv,.txt,.xlsx" onChange={async (e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          if (/\.xlsx$/i.test(f.name)) { const b = new Uint8Array(await f.arrayBuffer()); let bin = ''; for (let i = 0; i < b.length; i += 0x8000) bin += String.fromCharCode(...b.subarray(i, i + 0x8000)); setXlsx(btoa(bin)); setCsv(''); }
          else { setCsv(await f.text()); setXlsx(''); }
          setFileName(f.name); setPlan(null);
        }} />
        <input className="input" style={{ width: 150 }} inputMode="decimal" placeholder="Door price (NOK)" value={price} onChange={(e) => setPrice(e.target.value.replace(/[^\d.]/g, ''))} />
        <button className="btn btn-secondary" disabled={!csv && !xlsx} onClick={() => run(true)}>Preview</button>
        <button className="btn btn-primary" disabled={!plan || !plan.dryRun || plan.assigned.length === 0} onClick={() => run(false)}><Icon name="ph-upload-simple" />Import</button>
      </div>
      {fileName && <span className="text-muted" style={{ fontSize: 12 }}>{fileName}</span>}
      {err && <span style={{ color: 'var(--color-accent-300)', fontSize: 13 }}>{err}</span>}
      {plan && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
          {!plan.dryRun && <Tag cls="tag-accent">Imported: {plan.CREATED} new, {plan.UPDATED} updated, {plan.UNCHANGED} unchanged</Tag>}
          {plan.problems.map((p) => <span key={p} style={{ color: 'var(--color-accent-300)' }}>{p}</span>)}
          <span className="text-muted">Columns: {Object.entries(plan.columns).filter(([, v]) => v).map(([k, v]) => `${k} = "${v}"`).join(' · ')} · {plan.rows} rows</span>
          <table className="table" style={{ fontSize: 13 }}>
            <thead><tr><th>Event</th><th>Payments</th><th>Amount</th><th>Tickets</th></tr></thead>
            <tbody>{plan.assigned.map((a) => <tr key={a.eventId}><td>{a.eventTitle}</td><td>{a.payments}</td><td>{a.amount.toLocaleString('en-US')} NOK</td><td>{a.tickets}{a.estimated ? ` (${a.estimated} estimated)` : ''}</td></tr>)}
              {plan.assigned.length === 0 && <tr><td colSpan={4} className="text-muted">No payments fall on an event night.</td></tr>}</tbody>
          </table>
          <span className="text-muted">Left out: {Object.entries(plan.skipped).filter(([, n]) => n).map(([k, n]) => `${n} ${skipLabel[k] ?? k}`).join(' · ') || 'nothing'}</span>
        </div>
      )}
    </div>
  );
}
